import { createSlice } from "@reduxjs/toolkit";
import {
  getAgeingAnalysisReportAction,
  getBatchSummaryReportAction,
  getBatchVoucherReportAction,
  getCostOfGoodsAction,
  getDayBookReportsAction,
  getDetailedBalanceSheetAction,
  getDetailedProfitLossStatementAction,
  getDetailedTrialBalanceAction,
  getGeneralLedgerReportsAction,
  getMonthlyLedgerSummaryAction,
  getReceiptAndPaymentReportAction,
  getStockLimitAction,
  initialState,
} from ".";
import {
  getLedgerStatementAction,
  getTrialBalanceAction,
  getProfitLossStatementAction,
  getBalanceSheetAction,
  getStockReportsSummaryAction,
  getStockStatementAction,
} from ".";
import { reportsReducer } from "./reducer";
import { ReportsType } from "./reportsType";

export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: reportsReducer,
  extraReducers: {
    [getLedgerStatementAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getLedgerStatementAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;
      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        ledgerStatement: result?.data,
        ledgerStatementCount: result?.total_count,
        closingBalance: result?.closing_balance,
        openingBalance: result?.opening_balance,
        totalDebitCreditAmount: result?.total_debit_credit_amount,
      };
    },
    [getLedgerStatementAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      error: error?.message,
      success: 0,
    }),
    [getTrialBalanceAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getTrialBalanceAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;
      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        trialBalance: result?.data,
        trialBalanceCount: result?.total_count,
        totalBalances: {
          total_opening_balance: result?.total_opening_balance,
          total_closing_balance: result?.total_closing_balance,
          total_credit_amount: result?.total_credit_amount,
          total_debit_amount: result?.total_debit_amount,
        },
      };
    },
    [getTrialBalanceAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      error: error?.message,
      success: 0,
    }),
    [getDetailedTrialBalanceAction.pending.toString()]: (
      state: ReportsType
    ) => ({
      ...state,
      fetching: true,
    }),
    [getDetailedTrialBalanceAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;
      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        detailedTrialBalance: result,
      };
    },
    [getDetailedTrialBalanceAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      error: error?.message,
      success: 0,
    }),
    [getProfitLossStatementAction.pending.toString()]: (
      state: ReportsType
    ) => ({
      ...state,
      fetching: true,
    }),
    [getProfitLossStatementAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        expenseProfitLoss: result?.expenses,
        revenueProfitLoss: result?.income,
        totalProfitLossBalance: {
          total_expense: result?.total_expense,
          total_revenue: result?.total_revenue,
          net_profit: result?.net_profit,
        },
      };
    },
    [getProfitLossStatementAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      error: error?.message,
      success: 0,
    }),
    [getDetailedProfitLossStatementAction.pending.toString()]: (
      state: ReportsType
    ) => ({
      ...state,
      fetching: true,
    }),
    [getDetailedProfitLossStatementAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        detailedProfitLossData: result,
      };
    },
    [getDetailedProfitLossStatementAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      error: error?.message,
      success: 0,
    }),
    [getDetailedBalanceSheetAction.pending.toString()]: (
      state: ReportsType
    ) => ({
      ...state,
      fetching: true,
    }),
    [getDetailedBalanceSheetAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        detailedBalanceSheetData: result,
      };
    },
    [getDetailedBalanceSheetAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      error: error?.message,
      success: 0,
    }),
    [getStockReportsSummaryAction.pending.toString()]: (
      state: ReportsType
    ) => ({
      ...state,
      fetching: true,
    }),
    [getStockReportsSummaryAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { data } = payload;
      if (data.success === 0) {
        return {
          ...state,
          fetching: false,
          success: data.success,
          statusCode: data.status_code,
          error: data.message,
        };
      }
      return {
        ...state,
        fetching: false,
        stockReportsSummaryData: data.data,
        totalStockReportsSummaryData: {
          total_inwards_value: data.total_inwards_value,
          total_closing_value: data.total_closing_value,
          total_opening_value: data.total_opening_value,
          total_outwards_value: data.total_outwards_value,
        },
      };
    },
    [getStockReportsSummaryAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),
    [getStockStatementAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getStockStatementAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { data } = payload;
      if (data.success === 0) {
        return {
          ...state,
          fetching: false,
          success: data.success,
          statusCode: data.status_code,
          error: data.message,
        };
      }
      return {
        ...state,
        fetching: false,
        stockStatementData: data.data,
        totalStockStatementData: {
          total_inwards_qty: data.total_inwards_qty,
          total_inwards_rate: data.total_inwards_rate,
          total_inwards_value: data.total_inwards_value,
          total_outwards_qty: data.total_outwards_qty,
          total_outwards_rate: data.total_outwards_rate,
          total_outwards_value: data.total_outwards_value,
        },
      };
    },
    [getStockStatementAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),
    [getBalanceSheetAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getBalanceSheetAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        assetsBalanceSheetData: result?.assests_json,
        liabilitiesBalanceSheetData: result?.liabilities_json,
        equityBalanceSheetData: result?.equity_json,
        paymentBalanceSheetData: result?.payment_json,
        supplierBalanceSheetData: result?.supplier_json,
        customerBalanceSheetData: result?.customer_json,
        taxPayableBalanceSheetData: result?.tax_payable_json,
        taxReceivableBalanceSheetData: result?.tax_receivable_json,
        stockSummaryBalanceSheetData: result?.stock_summary_json,
        balanceSheetTotal: {
          customer_total_value: result?.customer_total_value,
          supplier_total_value: result?.supplier_total_value,
          assets_total_value: result?.assests_total_value,
          liabilities_total_value: result?.liabilities_total_value,
          equity_total_value: result?.equity_total_value,
          payment_total_value: result?.payment_total_value,
          net_profit: result?.net_profit,
          tax_payable_value: result?.tax_payable_value,
          tax_receivable_value: result?.tax_receivable_value,
          total_stock_summary_value: result?.total_stock_summary_value,
        },
      };
    },
    [getBalanceSheetAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),
    //Day Book Reports
    [getDayBookReportsAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getDayBookReportsAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        dayBookListData: result.data,
      };
    },
    [getDayBookReportsAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),
    // General Ledger Reports
    [getGeneralLedgerReportsAction.pending.toString()]: (
      state: ReportsType
    ) => ({
      ...state,
      fetching: true,
    }),
    [getGeneralLedgerReportsAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        generalLedgerListData: result,
      };
    },
    [getGeneralLedgerReportsAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),
    [getCostOfGoodsAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getCostOfGoodsAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        costOfGoods: result,
      };
    },
    [getCostOfGoodsAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      error: error?.message,
      success: 0,
    }),

    //
    [getAgeingAnalysisReportAction.pending.toString()]: (
      state: ReportsType
    ) => ({
      ...state,
      fetching: true,
    }),
    [getAgeingAnalysisReportAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        ageingAnalysisList: result?.data,
        ageingAnalysisTotalCount: result?.total_count,
        ageingAnalysis0To30DaysTotal: result?.total_sum?.["0_to_30_days"],
        ageingAnalysis30To60DaysTotal: result?.total_sum?.["30_to_60_days"],
        ageingAnalysis60To90DaysTotal: result?.total_sum?.["60_to_90_days"],
        ageingAnalysisMoreThan90DaysTotal:
          result?.total_sum?.["more_than_90_days"],
        ageingAnalysisTotalOfTotal: result?.total_sum?.["total_amount"],
      };
    },
    [getAgeingAnalysisReportAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),

    [getReceiptAndPaymentReportAction.pending.toString()]: (
      state: ReportsType
    ) => ({
      ...state,
      fetching: true,
    }),
    [getReceiptAndPaymentReportAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code } = payload;

      if (success === 0) {
        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }

      return {
        ...state,
        fetching: false,
        receiptAndPayment: result,
      };
    },
    [getReceiptAndPaymentReportAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),

    // stock limit
    [getStockLimitAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getStockLimitAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, result, status_code,data } = payload;
      if (data.success === 0) {
        return {
          ...state,
          fetching: false,
          success:data.success,
          statusCode: data.status_code,
          error: data.message,
        };
      }
      return {
        ...state,
        fetching: false,
        stocklimitData: data.result,
        stockLimitTotalCount:data.total_count
     
      };
    },
    [getStockLimitAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),

    // Batch Summary Report

    [getBatchSummaryReportAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getBatchSummaryReportAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const {data} = payload;
      if(data.success === 0) {
        return{
          ...state,
          fetching: false,
          success: data.success,
          statusCode: data.status_code,
          error: data.message,
        };
      }
      return {
        ...state,
        fetching: false,
        batchSummaryReportData: data.result,
        totalBatchSummaryData: {
          total_opening_balance: data.total_opening_balance,
          total_closing_balance: data.total_closing_balance,
          total_inwards_value: data.total_inwards_value,
          total_outwards_value: data.total_outwards_value,
          stock_item_name: data.stock_item_name,
        }
      };
    },
    [getBatchSummaryReportAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),

    // batch voucher report

    [getBatchVoucherReportAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getBatchVoucherReportAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const {data} = payload;
      if(data.success === 0) {
        return {
          ...state,
          fetching: false,
          success: data.success,
          statusCode: data.status_code,
          error: data. message,
        };
      }
      return {
        ...state,
        fetching: false,
        batchVoucherData: data.data,
        totalBatchVoucherData: {
          opening_qty: data.opening_qty,
          opening_value: data.opening_value,
          stock_item_name: data.stock_item_name,
          closing_qty: data.closing_qty,
          closing_value: data.closing_value,
          total_inwards_qty: data.total_inwards_qty,
          total_inwards_rate: data.total_inwards_rate,
          total_inwards_value: data.total_inwards_value,
          total_outwards_qty: data.total_outwards_qty,
          total_outwards_rate: data.total_outwards_rate,
          total_outwards_value: data.total_outwards_value,
          batch_details : {
            batch_id: data.batch_id,
            batch_number: data.batch_number,
            manufacturing_date: data.manufacturing_date,
            expiry_date: data.expiry_date,
          }
        }
      };
    },
    [getBatchVoucherReportAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      success: 0,
      error: error?.message,
    }),

    // monthly ledger summary

    [getMonthlyLedgerSummaryAction.pending.toString()]: (state: ReportsType) => ({
      ...state,
      fetching: true,
    }),
    [getMonthlyLedgerSummaryAction.fulfilled.toString()]: (
      state: ReportsType,
      { payload }
    ) => {
      const { success, message, data, status_code} = payload;
      if(success === 0) {
        return{
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          error: message,
        };
      }
      return {
        ...state,
        fetching: false,
        statusCode: status_code,
        monthlyLedgerSummaryTotal: data?.result?.grand_totals,
        monthlyLedgerSummaryData: data?.result?.monthly_summary,
      };
    },
    [getMonthlyLedgerSummaryAction.rejected.toString()]: (
      state: ReportsType,
      { error }
    ) => ({
      ...state,
      fetching: false,
      error: error?.message,
      success: 0,
    }),

  },
});

export const {
  resetLedgerStatement,
  resetTrialBalance,
  resetExpenseProfitLoss,
  resetRevenueProfitLoss,
  resetReportsError,
  resetReportsFetching,
  resetReportsSuccess,
  resetAssetsBalanceSheetData,
  resetCustomerBalanceSheetData,
  resetEquityBalanceSheetData,
  resetLiabilitiesBalanceSheetData,
  resetPaymentBalanceSheetData,
  resetSupplierBalanceSheetData,
  resetTaxPayableBalanceSheetData,
  resetTaxReceivableBalanceSheetData,
  resetStockReportsSummary,
  resetStockStatement,
  resetDetailedProfitLossStatement,
  resetTaxLedgerStatementCount,
  resetDetailedBalanceSheet,
  resetBalanceSheetTotal,
  resetDayBookListData,
  resetGeneralLedgerListData,
  resetCostOfGoodsData,
  resetAgeingAnalysisList,
  resetAgeingAnalysisTotalCount,
  resetStockSummaryBalanceSheetData,
  resetDetailedTrialBalance,
  resetReceiptAndPayment,
  resetStocklimitData,
  resetStockLimitTotalCount,
  resetBatchSummaryReportData,
  resetBatchVoucherReportData,
  resetMonthlyLedgerSummaryData,
  resetMonthlyLedgerSummaryTotal,
} = reportsSlice.actions;
